<script setup>
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import { ref, computed } from "vue";
import { useToast } from "primevue/usetoast";
import { useForm } from "@inertiajs/inertia-vue3";

const props = defineProps(['customer', 'menuItems', 'flash', 'onedrive']);

// Use real data from backend
const onedriveFolder = ref(props.onedrive);

// Component state
const showEditDialog = ref(false);
const showDeleteConfirmDialog = ref(false);
const folderUrl = ref(onedriveFolder.value?.folder_web_url || '');
const isProcessing = ref(false);
const isDeleting = ref(false);

const toast = useToast();


// Format date in German format
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
};


// Open folder in web browser
const openInWeb = () => {
    if (onedriveFolder.value?.folder_web_url) {
        window.open(onedriveFolder.value.folder_web_url, '_blank');
    }
};


// Show edit dialog
const showEditForm = () => {
    folderUrl.value = onedriveFolder.value?.folder_web_url || '';
    showEditDialog.value = true;
};

// Cancel edit
const cancelEdit = () => {
    showEditDialog.value = false;
    folderUrl.value = '';
};

// Validate OneDrive URL
const isValidUrl = computed(() => {
    if (!folderUrl.value) return false;
    
    // Accept both 1drv.ms and onedrive.live.com URLs
    return folderUrl.value.startsWith('https://1drv.ms/') || 
           folderUrl.value.startsWith('https://onedrive.live.com/');
});

// Handle errors from OneDrive API
const handleOneDriveErrors = (errors) => {
    if (errors.folder_web_url) {
        toast.add({ 
            severity: 'error', 
            summary: 'Fehler', 
            detail: errors.folder_web_url, 
            life: 5000 
        });
    } else {
        // Generic validation errors
        Object.keys(errors).forEach(field => {
            toast.add({
                severity: 'error',
                summary: 'Validierungsfehler',
                detail: errors[field],
                life: 5000
            });
        });
    }
};

// Save folder assignment
const saveFolder = () => {
    if (!isValidUrl.value) {
        toast.add({
            severity: 'error',
            summary: 'Ungültige URL',
            detail: 'Bitte geben Sie eine gültige OneDrive URL ein (1drv.ms oder onedrive.live.com).',
            life: 3000
        });
        return;
    }
    
    isProcessing.value = true;
    
    if (onedriveFolder.value) {
        // UPDATE existing folder
        const updateForm = useForm({
            folder_web_url: folderUrl.value
        });
        
        updateForm.put(`/customer-onedrives/${onedriveFolder.value.uuid}`, {
            preserveState: false,
            onSuccess: () => {
                showEditDialog.value = false;
                toast.add({ 
                    severity: 'success', 
                    summary: 'Erfolgreich', 
                    detail: 'OneDrive-Ordner wurde aktualisiert', 
                    life: 3000 
                });
            },
            onError: (errors) => {
                handleOneDriveErrors(errors);
            },
            onFinish: () => {
                isProcessing.value = false;
            }
        });
    } else {
        // CREATE new folder
        const createForm = useForm({
            customer_id: props.customer.id,
            folder_web_url: folderUrl.value
        });
        
        createForm.post('/customer-onedrives', {
            preserveState: false,
            onSuccess: () => {
                showEditDialog.value = false;
                toast.add({ 
                    severity: 'success', 
                    summary: 'Erfolgreich', 
                    detail: 'OneDrive-Ordner wurde verknüpft', 
                    life: 3000 
                });
            },
            onError: (errors) => {
                handleOneDriveErrors(errors);
            },
            onFinish: () => {
                isProcessing.value = false;
            }
        });
    }
};


// Show delete confirmation dialog
const confirmDelete = () => {
    showDeleteConfirmDialog.value = true;
};

// Delete OneDrive folder assignment
const deleteFolder = () => {
    isDeleting.value = true;
    
    const deleteForm = useForm({});
    
    deleteForm.delete(`/customer-onedrives/${onedriveFolder.value.uuid}`, {
        preserveState: false,
        onSuccess: () => {
            showDeleteConfirmDialog.value = false;
            toast.add({ 
                severity: 'success', 
                summary: 'Erfolgreich', 
                detail: 'OneDrive-Ordnerzuweisung wurde entfernt', 
                life: 3000 
            });
        },
        onError: () => {
            toast.add({ 
                severity: 'error', 
                summary: 'Fehler', 
                detail: 'Ordner konnte nicht entfernt werden', 
                life: 5000 
            });
        },
        onFinish: () => {
            isDeleting.value = false;
        }
    });
};
</script>

<template>
    <Layout :customer="customer" :menu-items="menuItems" :flash="flash">
        <!-- OneDrive Folder Section -->
        <div class="bg-white rounded-lg shadow-sm p-6">
            <h2 class="text-lg font-semibold mb-4">OneDrive Ordner</h2>
            
            <!-- Folder Assigned -->
            <div v-if="onedriveFolder" class="space-y-4">
                <div class="bg-gray-50 rounded p-4">
                    <p class="text-sm text-gray-600 break-all">{{ onedriveFolder.folder_web_url }}</p>
                </div>
                
                <div class="flex justify-between items-center">
                    <div class="flex gap-3">
                        <Button 
                            @click="openInWeb"
                            icon="pi pi-external-link"
                            label="In OneDrive öffnen"
                            class="p-button-primary"
                        />
                        <Button 
                            @click="showEditForm"
                            icon="pi pi-pencil"
                            label="Ordner ändern"
                            class="p-button-outlined"
                        />
                    </div>
                    <Button 
                        @click="confirmDelete"
                        icon="pi pi-trash"
                        class="p-button-outlined p-button-danger p-button-sm"
                        v-tooltip="'Ordner entfernen'"
                    />
                </div>
            </div>
            
            <!-- No Folder Assigned -->
            <div v-else class="bg-yellow-50 border border-yellow-200 rounded p-4">
                <p class="text-gray-700 mb-3">
                    Diesem Kunden wurde noch kein OneDrive Ordner zugewiesen.
                </p>
                <Button 
                    @click="showEditForm"
                    icon="pi pi-folder-open"
                    label="OneDrive Ordner zuweisen"
                    class="p-button-primary"
                />
            </div>
        </div>

        <!-- Edit Dialog -->
        <Dialog 
            v-model:visible="showEditDialog" 
            modal 
            header="OneDrive Ordner zuweisen"
            :style="{ width: '600px' }"
            :closable="!isProcessing"
        >
            <div class="space-y-4">
                <div>
                    <label for="folder_url" class="block text-sm font-medium mb-2">
                        OneDrive Ordner URL *
                    </label>
                    <InputText 
                        id="folder_url"
                        placeholder="In OneDrive: Ordner auswählen → Teilen → Link kopieren"
                        v-model="folderUrl"
                        class="w-full"
                        :disabled="isProcessing"
                    />
                    <small v-if="folderUrl && !isValidUrl" class="text-red-500">
                        Ungültige URL. Bitte verwenden Sie eine OneDrive URL (1drv.ms oder onedrive.live.com).
                    </small>
                </div>
                
                <!-- Instructions -->
                <div class="bg-blue-50 border border-blue-200 rounded p-4">
                    <h4 class="font-semibold mb-2">So erhalten Sie die OneDrive URL:</h4>
                    <ol class="list-decimal list-inside space-y-1 text-sm">
                        <li>Öffnen Sie OneDrive im Browser</li>
                        <li>Navigieren Sie zum gewünschten Ordner</li>
                        <li>Klicken Sie auf "Link kopieren"</li>
                        <li>Fügen Sie den kopierten Link hier ein</li>
                    </ol>
                    <p class="text-xs text-gray-600 mt-2">
                        <strong>Hinweis:</strong> Sowohl Freigabelinks (1drv.ms) als auch direkte OneDrive URLs werden akzeptiert.
                    </p>
                </div>
            </div>
            
            <template #footer>
                <Button 
                    label="Abbrechen" 
                    @click="cancelEdit"
                    class="p-button-text"
                    :disabled="isProcessing"
                />
                <Button 
                    label="Speichern" 
                    @click="saveFolder"
                    :disabled="!isValidUrl || isProcessing"
                    :loading="isProcessing"
                />
            </template>
        </Dialog>

        <!-- Delete Confirmation Dialog -->
        <Dialog 
            v-model:visible="showDeleteConfirmDialog" 
            modal 
            header="OneDrive Ordner entfernen"
            :style="{ width: '500px' }"
            :closable="!isDeleting"
        >
            <div class="space-y-4">
                <p class="text-gray-700">
                    Sind Sie sicher, dass Sie die OneDrive-Ordnerzuweisung entfernen möchten?
                </p>
                <div class="bg-yellow-50 border border-yellow-200 rounded p-4">
                    <p class="text-sm text-gray-600">
                        <strong>Hinweis:</strong> Dies entfernt nur die Verknüpfung. 
                        Der Ordner in OneDrive bleibt erhalten.
                    </p>
                </div>
            </div>
            
            <template #footer>
                <Button 
                    label="Abbrechen" 
                    @click="showDeleteConfirmDialog = false"
                    class="p-button-text"
                    :disabled="isDeleting"
                />
                <Button 
                    label="Ordner entfernen" 
                    @click="deleteFolder"
                    class="p-button-danger"
                    :loading="isDeleting"
                    icon="pi pi-trash"
                />
            </template>
        </Dialog>
    </Layout>
</template>

<script>
import Layout from "../Layouts/Customer.vue";
export default { components: { Layout } };
</script>